// UltraFlex Automation Builder - Visual workflow canvas with ReactFlow
// Full-suite: triggers, conditions, delays, actions, A/B splits, email editor, analytics
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import EmailDesigner from './EmailDesigner';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ReactFlow, Background, Controls, MiniMap, Panel,
  useNodesState, useEdgesState, addEdge, MarkerType,
  Handle, Position, useReactFlow, ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Zap, Mail, MessageSquare, Clock, GitBranch, PhoneCall,
  ArrowRight, Tag, Bell, UserCheck, Target, AlertTriangle,
  CreditCard, UserPlus, Building2, Inbox, Ticket, TimerOff,
  Plus, Trash2, Save, Play, Pause, X, ChevronLeft, Eye,
  Users, BarChart3, Settings2, Check, Copy, Shuffle,
  LogOut, Award, Hash, GripVertical, ChevronDown, ChevronRight,
  ArrowDown, Search, Filter, RefreshCw, ExternalLink,
  Type, Image, Link2, Bold, Italic, List,
  LayoutTemplate, Sparkles, Heart, TrendingUp, Calendar,
  Shield, Star, Rocket, Palette
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===================== CONSTANTS =====================
const TRIGGER_DEFS = {
  web_dropoff: { label: 'Web Drop-Off', icon: AlertTriangle, color: '#f59e0b', desc: 'Visitor abandons signup form' },
  new_member: { label: 'New Member', icon: UserPlus, color: '#22c55e', desc: 'Membership activated' },
  payment_failed: { label: 'Payment Failed', icon: CreditCard, color: '#ef4444', desc: 'DD collection fails' },
  cancellation_risk: { label: 'Cancel Risk', icon: AlertTriangle, color: '#f97316', desc: 'Cancellation signals detected' },
  lead_created: { label: 'New Lead', icon: Target, color: '#3b82f6', desc: 'Lead enters pipeline' },
  day_pass_purchased: { label: 'Day Pass', icon: Ticket, color: '#8b5cf6', desc: 'Day pass bought' },
  stage_change: { label: 'Stage Change', icon: ArrowRight, color: '#06b6d4', desc: 'Lead moves pipeline stage' },
  inactivity: { label: 'Inactivity', icon: TimerOff, color: '#6b7280', desc: 'No activity for X days' },
  tour_completed: { label: 'Tour Done', icon: Building2, color: '#6366f1', desc: 'Gym tour completed' },
  contact_form: { label: 'Contact Form', icon: Inbox, color: '#14b8a6', desc: 'Website enquiry received' },
};

const NODE_PALETTE = [
  { type: 'emailNode', label: 'Send Email', icon: Mail, color: '#22c55e', category: 'action' },
  { type: 'smsNode', label: 'Send SMS', icon: MessageSquare, color: '#3b82f6', category: 'action' },
  { type: 'delayNode', label: 'Wait / Delay', icon: Clock, color: '#f59e0b', category: 'timing' },
  { type: 'conditionNode', label: 'If / Else', icon: GitBranch, color: '#8b5cf6', category: 'logic' },
  { type: 'abSplitNode', label: 'A/B Split', icon: Shuffle, color: '#ec4899', category: 'logic' },
  { type: 'taskNode', label: 'Create Task', icon: PhoneCall, color: '#f97316', category: 'action' },
  { type: 'stageNode', label: 'Change Stage', icon: ArrowRight, color: '#06b6d4', category: 'action' },
  { type: 'tagNode', label: 'Add Tag', icon: Tag, color: '#ec4899', category: 'action' },
  { type: 'assignNode', label: 'Assign Staff', icon: UserCheck, color: '#14b8a6', category: 'action' },
  { type: 'notifyNode', label: 'Notification', icon: Bell, color: '#f59e0b', category: 'action' },
  { type: 'exitNode', label: 'Exit Journey', icon: LogOut, color: '#6b7280', category: 'control' },
];

const MERGE_TAGS = [
  { tag: '{{first_name}}', label: 'First Name' },
  { tag: '{{last_name}}', label: 'Last Name' },
  { tag: '{{email}}', label: 'Email' },
  { tag: '{{phone}}', label: 'Phone' },
  { tag: '{{gym_name}}', label: 'Gym Name' },
  { tag: '{{site_name}}', label: 'Site Name' },
  { tag: '{{plan_name}}', label: 'Plan Name' },
  { tag: '{{member_number}}', label: 'Member Number' },
  { tag: '{{join_date}}', label: 'Join Date' },
  { tag: '{{amount_due}}', label: 'Amount Due' },
  { tag: '{{unsubscribe_link}}', label: 'Unsubscribe Link' },
];

const CONDITION_TYPES = [
  { id: 'email_opened', label: 'Email Opened', desc: 'Did they open the previous email?' },
  { id: 'email_clicked', label: 'Link Clicked', desc: 'Did they click a link in the email?' },
  { id: 'member_status', label: 'Member Status', desc: 'Check current membership status' },
  { id: 'tag_exists', label: 'Has Tag', desc: 'Does the contact have a specific tag?' },
  { id: 'days_since_join', label: 'Days Since Join', desc: 'How many days since they joined?' },
  { id: 'site_location', label: 'Site Location', desc: 'Which gym location are they at?' },
  { id: 'plan_type', label: 'Plan Type', desc: 'What plan are they on?' },
  { id: 'engagement_score', label: 'Engagement Score', desc: 'Based on opens, clicks, visits' },
];

const PIPELINE_STAGES = ['new','contacted','qualified','tour_booked','tour_done','negotiation','joined','lost'];

// ===================== CUSTOM NODE COMPONENTS =====================

const baseNodeStyle = (color, selected) => ({
  background: '#1a1a2e',
  border: `2px solid ${selected ? color : '#2a2a4a'}`,
  borderRadius: '12px',
  padding: '0',
  minWidth: '220px',
  boxShadow: selected ? `0 0 20px ${color}30` : '0 4px 12px rgba(0,0,0,0.3)',
  transition: 'all 0.2s',
});

const NodeHeader = ({ icon: Icon, label, color, onDelete, id }) => (
  <div style={{ background: `${color}15`, borderBottom: `1px solid ${color}30`, borderRadius: '10px 10px 0 0', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{ background: `${color}25`, borderRadius: '6px', padding: '4px', display: 'flex' }}>
        <Icon size={14} color={color} />
      </div>
      <span style={{ fontSize: '12px', fontWeight: 600, color: '#e2e8f0' }}>{label}</span>
    </div>
    {id !== 'trigger' && (
      <button onClick={(e) => { e.stopPropagation(); onDelete?.(id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', opacity: 0.5 }}>
        <Trash2 size={12} color="#ef4444" />
      </button>
    )}
  </div>
);

const TriggerNode = ({ data, selected }) => {
  const trigger = TRIGGER_DEFS[data.triggerType] || TRIGGER_DEFS.lead_created;
  const TriggerIcon = trigger.icon;
  return (
    <div style={baseNodeStyle(trigger.color, selected)} data-testid="trigger-node">
      <NodeHeader icon={Zap} label="TRIGGER" color={trigger.color} id="trigger" />
      <div style={{ padding: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: `${trigger.color}10`, borderRadius: '8px', padding: '8px 10px', border: `1px solid ${trigger.color}20` }}>
          <TriggerIcon size={16} color={trigger.color} />
          <div>
            <p style={{ fontSize: '12px', fontWeight: 600, color: '#e2e8f0', margin: 0 }}>{trigger.label}</p>
            <p style={{ fontSize: '10px', color: '#94a3b8', margin: 0 }}>{trigger.desc}</p>
          </div>
        </div>
        {data.triggerConfig?.target_stage && (
          <div style={{ marginTop: '6px', fontSize: '10px', color: '#94a3b8', padding: '0 4px' }}>Stage: {data.triggerConfig.target_stage}</div>
        )}
        {data.triggerConfig?.days && (
          <div style={{ marginTop: '6px', fontSize: '10px', color: '#94a3b8', padding: '0 4px' }}>After {data.triggerConfig.days} days</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: trigger.color, width: 10, height: 10, border: '2px solid #1a1a2e' }} />
    </div>
  );
};

const EmailNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#22c55e', selected)} data-testid={`email-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#22c55e', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={Mail} label="Send Email" color="#22c55e" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <p style={{ fontSize: '11px', color: '#e2e8f0', margin: '0 0 2px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.subject || 'No subject set'}</p>
      <p style={{ fontSize: '10px', color: '#64748b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.previewText || 'Click to edit email content...'}</p>
      {data.stats && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #2a2a4a' }}>
          <span style={{ fontSize: '10px', color: '#22c55e' }}>{data.stats.sent || 0} sent</span>
          <span style={{ fontSize: '10px', color: '#3b82f6' }}>{data.stats.opened || 0} opened</span>
          <span style={{ fontSize: '10px', color: '#8b5cf6' }}>{data.stats.clicked || 0} clicked</span>
        </div>
      )}
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#22c55e', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const SmsNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#3b82f6', selected)} data-testid={`sms-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#3b82f6', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={MessageSquare} label="Send SMS" color="#3b82f6" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <p style={{ fontSize: '11px', color: '#e2e8f0', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.message || 'Click to set SMS message...'}</p>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#3b82f6', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const DelayNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#f59e0b', selected)} data-testid={`delay-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#f59e0b', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={Clock} label="Wait / Delay" color="#f59e0b" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ fontSize: '20px', fontWeight: 700, color: '#f59e0b' }}>{data.duration || 1}</span>
      <span style={{ fontSize: '12px', color: '#94a3b8' }}>{data.unit || 'hours'}</span>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#f59e0b', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const ConditionNode = ({ id, data, selected }) => {
  const cond = CONDITION_TYPES.find(c => c.id === data.conditionType) || { label: 'Select condition...', desc: '' };
  return (
    <div style={{ ...baseNodeStyle('#8b5cf6', selected), minWidth: '240px' }} data-testid={`condition-node-${id}`}>
      <Handle type="target" position={Position.Top} style={{ background: '#8b5cf6', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
      <NodeHeader icon={GitBranch} label="If / Else" color="#8b5cf6" onDelete={data.onDelete} id={id} />
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontSize: '11px', color: '#e2e8f0', margin: 0 }}>{cond.label}</p>
        <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>{cond.desc}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '10px' }}>
          <span style={{ color: '#22c55e', fontWeight: 600 }}>YES</span>
          <span style={{ color: '#ef4444', fontWeight: 600 }}>NO</span>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} id="yes" style={{ background: '#22c55e', width: 8, height: 8, border: '2px solid #1a1a2e', left: '30%' }} />
      <Handle type="source" position={Position.Bottom} id="no" style={{ background: '#ef4444', width: 8, height: 8, border: '2px solid #1a1a2e', left: '70%' }} />
    </div>
  );
};

const ABSplitNode = ({ id, data, selected }) => (
  <div style={{ ...baseNodeStyle('#ec4899', selected), minWidth: '240px' }} data-testid={`absplit-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#ec4899', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={Shuffle} label="A/B Split Test" color="#ec4899" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ background: '#3b82f620', color: '#3b82f6', borderRadius: '6px', padding: '4px 10px', fontWeight: 700 }}>A</div>
          <span style={{ color: '#94a3b8', fontSize: '10px' }}>{data.splitA || 50}%</span>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ background: '#f5920b20', color: '#f59e0b', borderRadius: '6px', padding: '4px 10px', fontWeight: 700 }}>B</div>
          <span style={{ color: '#94a3b8', fontSize: '10px' }}>{data.splitB || 50}%</span>
        </div>
      </div>
    </div>
    <Handle type="source" position={Position.Bottom} id="pathA" style={{ background: '#3b82f6', width: 8, height: 8, border: '2px solid #1a1a2e', left: '30%' }} />
    <Handle type="source" position={Position.Bottom} id="pathB" style={{ background: '#f59e0b', width: 8, height: 8, border: '2px solid #1a1a2e', left: '70%' }} />
  </div>
);

const TaskNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#f97316', selected)} data-testid={`task-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#f97316', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={PhoneCall} label="Create Task" color="#f97316" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <p style={{ fontSize: '11px', color: '#e2e8f0', margin: 0 }}>{data.taskSubject || 'Click to set task...'}</p>
      <p style={{ fontSize: '10px', color: '#64748b', margin: '2px 0 0' }}>{data.taskType || 'follow_up'} &middot; {data.priority || 'medium'}</p>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#f97316', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const StageNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#06b6d4', selected)} data-testid={`stage-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#06b6d4', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={ArrowRight} label="Change Stage" color="#06b6d4" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <p style={{ fontSize: '11px', color: '#e2e8f0', margin: 0, textTransform: 'capitalize' }}>{(data.targetStage || 'select stage').replace(/_/g, ' ')}</p>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#06b6d4', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const TagNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#ec4899', selected)} data-testid={`tag-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#ec4899', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={Tag} label="Add Tag" color="#ec4899" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      {data.tagName ? (
        <span style={{ background: '#ec489920', color: '#ec4899', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 500 }}>{data.tagName}</span>
      ) : (
        <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Click to set tag...</p>
      )}
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#ec4899', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const AssignNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#14b8a6', selected)} data-testid={`assign-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#14b8a6', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={UserCheck} label="Assign Staff" color="#14b8a6" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <p style={{ fontSize: '11px', color: '#e2e8f0', margin: 0 }}>{data.assigneeName || data.assignMode === 'round_robin' ? 'Round Robin' : 'Click to assign...'}</p>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#14b8a6', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const NotifyNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#f59e0b', selected)} data-testid={`notify-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#f59e0b', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={Bell} label="In-App Notification" color="#f59e0b" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <p style={{ fontSize: '11px', color: '#e2e8f0', margin: 0 }}>{data.message || 'Click to set message...'}</p>
    </div>
    <Handle type="source" position={Position.Bottom} style={{ background: '#f59e0b', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
  </div>
);

const ExitNode = ({ id, data, selected }) => (
  <div style={baseNodeStyle('#6b7280', selected)} data-testid={`exit-node-${id}`}>
    <Handle type="target" position={Position.Top} style={{ background: '#6b7280', width: 8, height: 8, border: '2px solid #1a1a2e' }} />
    <NodeHeader icon={LogOut} label="Exit Journey" color="#6b7280" onDelete={data.onDelete} id={id} />
    <div style={{ padding: '10px 12px' }}>
      <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>{data.exitReason || 'Journey ends here'}</p>
    </div>
  </div>
);

// ===================== NODE TYPES REGISTRY =====================
const nodeTypes = {
  triggerNode: TriggerNode,
  emailNode: EmailNode,
  smsNode: SmsNode,
  delayNode: DelayNode,
  conditionNode: ConditionNode,
  abSplitNode: ABSplitNode,
  taskNode: TaskNode,
  stageNode: StageNode,
  tagNode: TagNode,
  assignNode: AssignNode,
  notifyNode: NotifyNode,
  exitNode: ExitNode,
};

// ===================== PROPERTIES PANEL =====================
const PropertiesPanel = ({ node, onUpdate, onClose, staffList, onOpenEmailDesigner }) => {
  if (!node) return null;
  const data = node.data || {};
  const update = (field, value) => onUpdate(node.id, { ...data, [field]: value });

  return (
    <div className="w-80 bg-[#111827] border-l border-[#1e293b] h-full overflow-y-auto flex-shrink-0" data-testid="properties-panel">
      <div className="sticky top-0 bg-[#111827] z-10 p-4 border-b border-[#1e293b] flex justify-between items-center">
        <h3 className="text-white font-semibold text-sm">Properties</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={16} /></button>
      </div>
      <div className="p-4 space-y-4">
        {/* TRIGGER PROPERTIES */}
        {node.type === 'triggerNode' && (
          <>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-2">Trigger Event</label>
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {Object.entries(TRIGGER_DEFS).map(([key, t]) => {
                  const TIcon = t.icon;
                  return (
                    <button key={key} onClick={() => { update('triggerType', key); update('triggerConfig', {}); }}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-left text-xs transition-all ${data.triggerType === key ? 'ring-1' : 'hover:bg-[#1e293b]'}`}
                      style={data.triggerType === key ? { background: `${t.color}15`, borderColor: t.color, ringColor: t.color } : {}}>
                      <TIcon size={14} style={{ color: t.color }} />
                      <div>
                        <p className="text-white font-medium">{t.label}</p>
                        <p className="text-gray-500 text-[10px]">{t.desc}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            {data.triggerType === 'stage_change' && (
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-1">Target Stage</label>
                <select value={data.triggerConfig?.target_stage || ''} onChange={e => update('triggerConfig', { ...data.triggerConfig, target_stage: e.target.value })}
                  className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                  <option value="">Select...</option>
                  {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
            )}
            {data.triggerType === 'inactivity' && (
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-1">Days of inactivity</label>
                <input type="number" min="1" value={data.triggerConfig?.days || 7} onChange={e => update('triggerConfig', { ...data.triggerConfig, days: parseInt(e.target.value) || 7 })}
                  className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs" />
              </div>
            )}
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Exit Condition</label>
              <select value={data.exitCondition || 'none'} onChange={e => update('exitCondition', e.target.value)}
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                <option value="none">No exit condition</option>
                <option value="goal_reached">Goal reached (e.g., became member)</option>
                <option value="manual_exit">Manual removal only</option>
                <option value="max_duration">Max duration reached</option>
              </select>
              {data.exitCondition === 'max_duration' && (
                <div className="flex items-center gap-2 mt-2">
                  <input type="number" min="1" value={data.maxDurationDays || 30} onChange={e => update('maxDurationDays', parseInt(e.target.value) || 30)}
                    className="w-20 bg-[#0f172a] border border-[#1e293b] rounded px-2 py-1.5 text-white text-xs" />
                  <span className="text-gray-400 text-xs">days</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* EMAIL PROPERTIES */}
        {node.type === 'emailNode' && (
          <>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Subject Line</label>
              <input value={data.subject || ''} onChange={e => update('subject', e.target.value)} placeholder="Welcome to UltraFlex!"
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-sm" data-testid="email-subject-input" />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {MERGE_TAGS.slice(0, 4).map(mt => (
                  <button key={mt.tag} onClick={() => update('subject', (data.subject || '') + ' ' + mt.tag)}
                    className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-[9px] font-mono hover:bg-emerald-500/20">{mt.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Preview Text</label>
              <input value={data.previewText || ''} onChange={e => update('previewText', e.target.value)} placeholder="Shows in inbox preview..."
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs" />
            </div>

            {/* Visual Email Designer button */}
            <button onClick={() => onOpenEmailDesigner(node)}
              className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#E31837] to-[#c41530] text-white rounded-lg text-sm font-semibold hover:from-[#c41530] hover:to-[#a11228] transition-all shadow-lg shadow-red-500/20"
              data-testid="open-email-designer-btn">
              <Palette size={16} />
              {data.emailBlocks?.length > 0 ? 'Edit Email Design' : 'Design Email Content'}
            </button>

            {/* Email design status */}
            {data.emailBlocks && data.emailBlocks.length > 0 && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Check size={14} className="text-emerald-400" />
                  <span className="text-emerald-400 text-xs font-medium">Visual design applied</span>
                </div>
                <p className="text-gray-500 text-[10px]">{data.emailBlocks.length} content blocks configured</p>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-[#0f172a] rounded-lg border border-[#1e293b]">
              <span className="text-gray-400 text-xs">Track Opens</span>
              <button onClick={() => update('trackOpens', !data.trackOpens)}
                className={`w-9 h-5 rounded-full transition-colors ${data.trackOpens !== false ? 'bg-[#22c55e]' : 'bg-gray-600'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${data.trackOpens !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0f172a] rounded-lg border border-[#1e293b]">
              <span className="text-gray-400 text-xs">Track Clicks</span>
              <button onClick={() => update('trackClicks', !data.trackClicks)}
                className={`w-9 h-5 rounded-full transition-colors ${data.trackClicks !== false ? 'bg-[#3b82f6]' : 'bg-gray-600'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${data.trackClicks !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </>
        )}

        {/* SMS PROPERTIES */}
        {node.type === 'smsNode' && (
          <>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">SMS Message</label>
              <textarea value={data.message || ''} onChange={e => update('message', e.target.value)} rows={4} placeholder="Hi {{first_name}}, ..."
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs resize-none" />
              <div className="flex justify-between mt-1.5">
                <p className={`text-[10px] ${(data.message || '').length > 160 ? 'text-amber-400' : 'text-gray-500'}`}>
                  {(data.message || '').length}/160 characters
                </p>
                <p className="text-[10px] text-gray-500">
                  {Math.ceil((data.message || '').length / 160) || 1} segment{Math.ceil((data.message || '').length / 160) > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1.5">Insert Merge Tag</label>
              <div className="flex flex-wrap gap-1">
                {MERGE_TAGS.slice(0, 5).map(mt => (
                  <button key={mt.tag} onClick={() => update('message', (data.message || '') + mt.tag)}
                    className="px-2 py-1 bg-[#1e293b] text-[#3b82f6] rounded text-[10px] font-mono hover:bg-[#2a3a5a]">{mt.label}</button>
                ))}
              </div>
            </div>
            {/* Phone preview */}
            <div>
              <label className="text-gray-400 text-[10px] font-medium block mb-2">SMS Preview</label>
              <div className="mx-auto w-48 bg-[#000] rounded-[20px] p-2 border-2 border-[#333]">
                <div className="bg-[#1c1c1e] rounded-[14px] p-3 min-h-[120px]">
                  <div className="text-center mb-2">
                    <span className="text-gray-500 text-[8px]">UltraFlex Gym</span>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-[#333] rounded-2xl rounded-bl-md px-3 py-2 max-w-[160px]">
                      <p className="text-white text-[9px] leading-relaxed whitespace-pre-wrap break-words">
                        {(data.message || 'Your message will appear here...').replace(/\{\{(\w+)\}\}/g, (_, k) => {
                          const map = { first_name: 'John', last_name: 'Smith', site_name: 'UltraFlex City', email: 'john@email.com' };
                          return map[k] || `{{${k}}}`;
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* DELAY PROPERTIES */}
        {node.type === 'delayNode' && (
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-2">Wait Duration</label>
            <div className="flex items-center gap-2">
              <input type="number" min="1" value={data.duration || 1} onChange={e => update('duration', parseInt(e.target.value) || 1)}
                className="w-20 bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-sm font-bold text-center" />
              <select value={data.unit || 'hours'} onChange={e => update('unit', e.target.value)}
                className="flex-1 bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
              </select>
            </div>
            <div className="mt-3">
              <label className="text-gray-400 text-xs font-medium block mb-1">Send Window</label>
              <select value={data.sendWindow || 'anytime'} onChange={e => update('sendWindow', e.target.value)}
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                <option value="anytime">Any time</option>
                <option value="business_hours">Business hours (9am-6pm)</option>
                <option value="morning">Morning (8am-12pm)</option>
                <option value="afternoon">Afternoon (12pm-5pm)</option>
                <option value="evening">Evening (5pm-9pm)</option>
              </select>
            </div>
          </div>
        )}

        {/* CONDITION PROPERTIES */}
        {node.type === 'conditionNode' && (
          <>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-2">Condition Type</label>
              <div className="space-y-1.5">
                {CONDITION_TYPES.map(c => (
                  <button key={c.id} onClick={() => update('conditionType', c.id)}
                    className={`w-full text-left p-2.5 rounded-lg text-xs transition-all ${data.conditionType === c.id ? 'bg-[#8b5cf6]/15 ring-1 ring-[#8b5cf6]' : 'bg-[#0f172a] hover:bg-[#1e293b]'}`}>
                    <p className="text-white font-medium">{c.label}</p>
                    <p className="text-gray-500 text-[10px]">{c.desc}</p>
                  </button>
                ))}
              </div>
            </div>
            {data.conditionType === 'member_status' && (
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-1">Status equals</label>
                <select value={data.conditionValue || ''} onChange={e => update('conditionValue', e.target.value)}
                  className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                  <option value="">Select...</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            )}
            {data.conditionType === 'days_since_join' && (
              <div className="flex items-center gap-2">
                <select value={data.conditionOperator || 'gt'} onChange={e => update('conditionOperator', e.target.value)}
                  className="bg-[#0f172a] border border-[#1e293b] rounded px-2 py-1.5 text-white text-xs">
                  <option value="gt">More than</option>
                  <option value="lt">Less than</option>
                  <option value="eq">Exactly</option>
                </select>
                <input type="number" min="1" value={data.conditionValue || 7} onChange={e => update('conditionValue', parseInt(e.target.value))}
                  className="w-16 bg-[#0f172a] border border-[#1e293b] rounded px-2 py-1.5 text-white text-xs text-center" />
                <span className="text-gray-400 text-xs">days</span>
              </div>
            )}
          </>
        )}

        {/* A/B SPLIT PROPERTIES */}
        {node.type === 'abSplitNode' && (
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-2">Split Ratio</label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-blue-400 text-[10px] font-bold block mb-1">Path A</label>
                <input type="number" min="1" max="99" value={data.splitA || 50} onChange={e => { const v = Math.min(99, Math.max(1, parseInt(e.target.value) || 50)); update('splitA', v); update('splitB', 100 - v); }}
                  className="w-full bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-white text-sm text-center font-bold" />
              </div>
              <span className="text-gray-500 text-xs mt-4">/</span>
              <div className="flex-1">
                <label className="text-amber-400 text-[10px] font-bold block mb-1">Path B</label>
                <input type="number" disabled value={data.splitB || 50}
                  className="w-full bg-[#0f172a] border border-[#1e293b] rounded px-3 py-2 text-gray-400 text-sm text-center font-bold" />
              </div>
            </div>
            <div className="mt-3 h-2 bg-[#0f172a] rounded-full overflow-hidden flex">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${data.splitA || 50}%` }} />
              <div className="h-full bg-amber-500 transition-all" style={{ width: `${data.splitB || 50}%` }} />
            </div>
          </div>
        )}

        {/* TASK PROPERTIES */}
        {node.type === 'taskNode' && (
          <>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Task Subject</label>
              <input value={data.taskSubject || ''} onChange={e => update('taskSubject', e.target.value)} placeholder="Follow up with lead"
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs" />
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Task Type</label>
              <select value={data.taskType || 'follow_up'} onChange={e => update('taskType', e.target.value)}
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                <option value="follow_up">Follow Up Call</option>
                <option value="email">Email</option>
                <option value="meeting">Meeting</option>
                <option value="tour">Tour</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Priority</label>
              <select value={data.priority || 'medium'} onChange={e => update('priority', e.target.value)}
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Due (days from now)</label>
              <input type="number" min="0" value={data.dueDays || 1} onChange={e => update('dueDays', parseInt(e.target.value) || 1)}
                className="w-20 bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs" />
            </div>
          </>
        )}

        {/* STAGE PROPERTIES */}
        {node.type === 'stageNode' && (
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-1">Move to Stage</label>
            <select value={data.targetStage || ''} onChange={e => update('targetStage', e.target.value)}
              className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
              <option value="">Select stage...</option>
              {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        )}

        {/* TAG PROPERTIES */}
        {node.type === 'tagNode' && (
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-1">Tag Name</label>
            <input value={data.tagName || ''} onChange={e => update('tagName', e.target.value)} placeholder="e.g. hot-lead, vip, at-risk"
              className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs" />
          </div>
        )}

        {/* ASSIGN PROPERTIES */}
        {node.type === 'assignNode' && (
          <>
            <div>
              <label className="text-gray-400 text-xs font-medium block mb-1">Assignment Mode</label>
              <select value={data.assignMode || 'specific'} onChange={e => update('assignMode', e.target.value)}
                className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                <option value="specific">Specific Staff</option>
                <option value="round_robin">Round Robin</option>
                <option value="least_loaded">Least Loaded</option>
              </select>
            </div>
            {data.assignMode === 'specific' && staffList?.length > 0 && (
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-1">Staff Member</label>
                <select value={data.assigneeId || ''} onChange={e => { const s = staffList.find(st => st.id === e.target.value); update('assigneeId', e.target.value); update('assigneeName', s ? `${s.first_name} ${s.last_name}` : ''); }}
                  className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
                  <option value="">Select...</option>
                  {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        {/* NOTIFY PROPERTIES */}
        {node.type === 'notifyNode' && (
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-1">Notification Message</label>
            <textarea value={data.message || ''} onChange={e => update('message', e.target.value)} rows={3} placeholder="New lead requires attention..."
              className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs resize-none" />
          </div>
        )}

        {/* EXIT PROPERTIES */}
        {node.type === 'exitNode' && (
          <div>
            <label className="text-gray-400 text-xs font-medium block mb-1">Exit Reason</label>
            <select value={data.exitReason || ''} onChange={e => update('exitReason', e.target.value)}
              className="w-full bg-[#0f172a] border border-[#1e293b] rounded-lg px-3 py-2 text-white text-xs">
              <option value="">Journey complete</option>
              <option value="goal_achieved">Goal achieved</option>
              <option value="unsubscribed">Contact unsubscribed</option>
              <option value="timeout">Max duration reached</option>
              <option value="manual">Manual removal</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== ENROLLMENT DASHBOARD =====================
const EnrollmentDashboard = ({ journeyId, headers }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [stats, setStats] = useState({ active: 0, completed: 0, exited: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const params = new URLSearchParams({ page_size: '50' });
      if (statusFilter) params.append('status', statusFilter);
      if (journeyId) params.append('journey_id', journeyId);
      try {
        const res = await fetch(`${API_URL}/api/crm/automations/enrollments?${params}`, { headers });
        const data = await res.json();
        setEnrollments(data.items || []);
        setStats({ active: data.active_count || 0, completed: data.completed_count || 0, exited: data.exited_count || 0, total: data.total || 0 });
      } catch { setEnrollments([]); }
      setLoading(false);
    };
    fetchData();
  }, [journeyId, statusFilter]);

  return (
    <div className="space-y-4" data-testid="enrollment-dashboard">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total Enrolled', value: stats.total, color: 'text-white' },
          { label: 'Active', value: stats.active, color: 'text-emerald-400' },
          { label: 'Completed', value: stats.completed, color: 'text-blue-400' },
          { label: 'Exited', value: stats.exited, color: 'text-gray-400' },
        ].map(s => (
          <div key={s.label} className="bg-[#111827] rounded-xl border border-[#1e293b] p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-gray-500 text-xs mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['', 'active', 'completed', 'exited'].map(f => (
          <button key={f} onClick={() => setStatusFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${statusFilter === f ? 'bg-[#dc2626] text-white' : 'bg-[#1e293b] text-gray-400 hover:text-white'}`}>
            {f || 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-[#111827] rounded-xl border border-[#1e293b] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1e293b]">
              <th className="px-4 py-3 text-left text-xs text-gray-400 font-medium">Contact</th>
              <th className="px-4 py-3 text-left text-xs text-gray-400 font-medium">Current Step</th>
              <th className="px-4 py-3 text-left text-xs text-gray-400 font-medium">Status</th>
              <th className="px-4 py-3 text-left text-xs text-gray-400 font-medium">Enrolled</th>
              <th className="px-4 py-3 text-left text-xs text-gray-400 font-medium">Next Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1e293b]">
            {loading ? (
              <tr><td colSpan="5" className="px-4 py-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-[#dc2626] mx-auto" /></td></tr>
            ) : enrollments.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500 text-sm">No enrollments found</td></tr>
            ) : enrollments.map(e => (
              <tr key={e.id} className="hover:bg-[#1e293b]/50">
                <td className="px-4 py-3">
                  <p className="text-white text-sm font-medium">{e.contact_name || '-'}</p>
                  <p className="text-gray-500 text-xs">{e.contact_email}</p>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs bg-[#1e293b] text-gray-300 px-2 py-1 rounded">Step {(e.current_step || 0) + 1}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${e.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : e.status === 'completed' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'}`}>{e.status}</span>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-'}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{e.next_action_at ? new Date(e.next_action_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================== JOURNEY ANALYTICS =====================
const JourneyAnalytics = ({ journeyId, nodes, headers }) => {
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await fetch(`${API_URL}/api/crm/automations/${journeyId}/analytics`, { headers });
        if (res.ok) setAnalytics(await res.json());
        else setAnalytics({ total_enrolled: 0, conversion_rate: 0, avg_completion_days: 0, step_stats: [], email_stats: { sent: 0, opened: 0, clicked: 0, open_rate: 0, click_rate: 0 } });
      } catch { setAnalytics({ total_enrolled: 0, conversion_rate: 0, avg_completion_days: 0, step_stats: [], email_stats: { sent: 0, opened: 0, clicked: 0, open_rate: 0, click_rate: 0 } }); }
    };
    if (journeyId) fetchAnalytics();
  }, [journeyId]);

  if (!analytics) return <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-[#dc2626] mx-auto" /></div>;

  const emailNodes = (nodes || []).filter(n => n.type === 'emailNode');

  return (
    <div className="space-y-6" data-testid="journey-analytics">
      {/* Top Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Enrolled', value: analytics.total_enrolled, icon: Users, color: '#e2e8f0' },
          { label: 'Conversion Rate', value: `${((analytics.conversion_rate || 0) * 100).toFixed(1)}%`, icon: Target, color: '#22c55e' },
          { label: 'Avg. Completion', value: `${analytics.avg_completion_days || 0}d`, icon: Clock, color: '#f59e0b' },
          { label: 'Active Now', value: analytics.active_count || 0, icon: Play, color: '#3b82f6' },
        ].map(s => (
          <div key={s.label} className="bg-[#111827] rounded-xl border border-[#1e293b] p-4">
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={16} color={s.color} />
              <span className="text-gray-400 text-xs">{s.label}</span>
            </div>
            <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Email Performance */}
      <div className="bg-[#111827] rounded-xl border border-[#1e293b] p-5">
        <h3 className="text-white font-semibold text-sm mb-4">Email Performance</h3>
        <div className="grid grid-cols-5 gap-4">
          {[
            { label: 'Sent', value: analytics.email_stats?.sent || 0, color: '#e2e8f0' },
            { label: 'Delivered', value: analytics.email_stats?.delivered || analytics.email_stats?.sent || 0, color: '#22c55e' },
            { label: 'Opened', value: analytics.email_stats?.opened || 0, color: '#3b82f6' },
            { label: 'Clicked', value: analytics.email_stats?.clicked || 0, color: '#8b5cf6' },
            { label: 'Bounced', value: analytics.email_stats?.bounced || 0, color: '#ef4444' },
          ].map(s => (
            <div key={s.label} className="text-center">
              <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
              <p className="text-gray-500 text-xs">{s.label}</p>
            </div>
          ))}
        </div>
        {analytics.email_stats?.sent > 0 && (
          <div className="mt-4 flex gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">Open Rate</span>
                <span className="text-blue-400">{((analytics.email_stats?.open_rate || 0) * 100).toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-[#0f172a] rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${(analytics.email_stats?.open_rate || 0) * 100}%` }} />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-400">Click Rate</span>
                <span className="text-purple-400">{((analytics.email_stats?.click_rate || 0) * 100).toFixed(1)}%</span>
              </div>
              <div className="h-2 bg-[#0f172a] rounded-full overflow-hidden">
                <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(analytics.email_stats?.click_rate || 0) * 100}%` }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step Funnel */}
      <div className="bg-[#111827] rounded-xl border border-[#1e293b] p-5">
        <h3 className="text-white font-semibold text-sm mb-4">Step Completion Funnel</h3>
        <div className="space-y-2">
          {(nodes || []).filter(n => n.type !== 'triggerNode').map((node, i) => {
            const stepStat = (analytics.step_stats || [])[i] || {};
            const reached = stepStat.reached || 0;
            const maxReached = (analytics.step_stats || []).reduce((max, s) => Math.max(max, s?.reached || 0), 1);
            const pct = maxReached > 0 ? (reached / maxReached) * 100 : 0;
            const palette = NODE_PALETTE.find(p => p.type === node.type);
            return (
              <div key={node.id} className="flex items-center gap-3">
                <span className="text-gray-500 text-xs w-16 text-right">Step {i + 1}</span>
                <div className="flex-1 h-7 bg-[#0f172a] rounded-lg overflow-hidden relative">
                  <div className="h-full rounded-lg transition-all" style={{ width: `${pct}%`, background: palette?.color || '#6b7280' }} />
                  <span className="absolute inset-0 flex items-center px-3 text-xs text-white font-medium">
                    {palette?.label || node.type} ({reached})
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ===================== JOURNEY TEMPLATES =====================
const EDGE_DEFAULTS = { type: 'smoothstep', animated: true, style: { stroke: '#4a5568', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4a5568' } };
const mkEdge = (src, tgt, srcHandle, tgtHandle) => ({ id: `e-${src}-${tgt}${srcHandle ? '-' + srcHandle : ''}`, source: src, target: tgt, sourceHandle: srcHandle || undefined, targetHandle: tgtHandle || undefined, ...EDGE_DEFAULTS });
const X_CENTER = 300;

const JOURNEY_TEMPLATES = [
  {
    id: 'blank',
    name: 'Blank Canvas',
    desc: 'Start from scratch with an empty workflow',
    icon: Plus,
    color: '#6b7280',
    category: 'starter',
    triggerType: 'lead_created',
    nodes: [],
    edges: [],
  },
  {
    id: 'new_lead_welcome',
    name: 'New Lead Welcome Sequence',
    desc: '5-step nurture: welcome email, benefits breakdown, engagement check, follow-up call, and stage progression',
    icon: Sparkles,
    color: '#3b82f6',
    category: 'lead_gen',
    triggerType: 'lead_created',
    nodeCount: 10,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'lead_created', triggerConfig: {}, exitCondition: 'goal_reached' }, deletable: false },
      { id: 'email-welcome', type: 'emailNode', position: { x: X_CENTER, y: 140 }, data: { subject: 'Welcome to UltraFlex, {{first_name}}!', previewText: 'Your fitness journey starts here', body: 'Hi {{first_name}},\n\nThanks for your interest in UltraFlex Gym! We\'re thrilled you\'re considering joining our community.\n\nHere\'s what makes us different:\n- State-of-the-art equipment across all 11 locations\n- No lock-in contracts\n- Free fitness assessment for new members\n- 24/7 access at most sites\n\nReady to take the next step? Book a free tour at {{site_name}} and see for yourself.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'tag-new', type: 'tagNode', position: { x: X_CENTER, y: 280 }, data: { tagName: 'new-lead' }},
      { id: 'delay-1', type: 'delayNode', position: { x: X_CENTER, y: 400 }, data: { duration: 2, unit: 'days', sendWindow: 'morning' }},
      { id: 'email-benefits', type: 'emailNode', position: { x: X_CENTER, y: 540 }, data: { subject: '5 reasons members love UltraFlex', previewText: 'See what you\'ve been missing', body: 'Hi {{first_name}},\n\nWondering what life at UltraFlex looks like? Here are 5 reasons our members stay:\n\n1. Flexible memberships — no annual contracts\n2. Group classes from HIIT to yoga, included free\n3. Personal training from certified PTs\n4. Clean, modern facilities with premium equipment\n5. A supportive community that keeps you motivated\n\nWe\'d love to show you around {{site_name}}. Book your free tour today!\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-2', type: 'delayNode', position: { x: X_CENTER, y: 680 }, data: { duration: 3, unit: 'days' }},
      { id: 'cond-opened', type: 'conditionNode', position: { x: X_CENTER, y: 820 }, data: { conditionType: 'email_opened' }},
      { id: 'task-call', type: 'taskNode', position: { x: X_CENTER - 160, y: 980 }, data: { taskSubject: 'Follow up call — engaged lead', taskType: 'follow_up', priority: 'high', dueDays: 1 }},
      { id: 'email-reengage', type: 'emailNode', position: { x: X_CENTER + 160, y: 980 }, data: { subject: 'Still interested, {{first_name}}?', previewText: 'We saved you a spot', body: 'Hi {{first_name}},\n\nWe noticed you haven\'t had a chance to check us out yet. No worries — we\'re here whenever you\'re ready.\n\nAs a reminder, your first visit is completely free. Just pop into {{site_name}} and ask for a tour.\n\nSee you soon!\nThe UltraFlex Team\n\n{{unsubscribe_link}}' }},
      { id: 'stage-contacted', type: 'stageNode', position: { x: X_CENTER, y: 1140 }, data: { targetStage: 'contacted' }},
    ],
    edges: [
      mkEdge('trigger', 'email-welcome'), mkEdge('email-welcome', 'tag-new'), mkEdge('tag-new', 'delay-1'),
      mkEdge('delay-1', 'email-benefits'), mkEdge('email-benefits', 'delay-2'), mkEdge('delay-2', 'cond-opened'),
      mkEdge('cond-opened', 'task-call', 'yes'), mkEdge('cond-opened', 'email-reengage', 'no'),
      mkEdge('task-call', 'stage-contacted'), mkEdge('email-reengage', 'stage-contacted'),
    ],
  },
  {
    id: 'dropoff_recovery',
    name: 'Web Drop-Off Recovery',
    desc: 'Recover abandoned signup forms with timed email + SMS nudges and staff follow-up',
    icon: AlertTriangle,
    color: '#f59e0b',
    category: 'lead_gen',
    triggerType: 'web_dropoff',
    nodeCount: 9,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'web_dropoff', triggerConfig: {}, exitCondition: 'goal_reached' }, deletable: false },
      { id: 'delay-1h', type: 'delayNode', position: { x: X_CENTER, y: 130 }, data: { duration: 1, unit: 'hours' }},
      { id: 'email-recover', type: 'emailNode', position: { x: X_CENTER, y: 260 }, data: { subject: 'You were so close, {{first_name}}!', previewText: 'Complete your UltraFlex registration', body: 'Hi {{first_name}},\n\nWe noticed you started signing up for UltraFlex but didn\'t quite finish. No problem — these things happen!\n\nYour details are saved, so you can pick up right where you left off. It only takes 2 minutes to complete.\n\nJoin now and get started at {{site_name}} as early as tomorrow.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-24h', type: 'delayNode', position: { x: X_CENTER, y: 400 }, data: { duration: 24, unit: 'hours' }},
      { id: 'cond-opened', type: 'conditionNode', position: { x: X_CENTER, y: 530 }, data: { conditionType: 'email_opened' }},
      { id: 'sms-nudge', type: 'smsNode', position: { x: X_CENTER - 160, y: 700 }, data: { message: 'Hi {{first_name}}! Your UltraFlex signup is almost done. Finish in 2 mins and start training this week: {{site_name}}' }},
      { id: 'email-offer', type: 'emailNode', position: { x: X_CENTER + 160, y: 700 }, data: { subject: 'A little incentive to join UltraFlex', previewText: 'Special offer just for you', body: 'Hi {{first_name}},\n\nWe really want you on board! To sweeten the deal, here\'s what\'s waiting:\n\n- No joining fee when you sign up this week\n- Free personal training taster session\n- Full access to all {{site_name}} facilities from day one\n\nDon\'t miss out — this offer won\'t last forever.\n\nSee you soon,\nThe UltraFlex Team\n\n{{unsubscribe_link}}' }},
      { id: 'task-followup', type: 'taskNode', position: { x: X_CENTER, y: 870 }, data: { taskSubject: 'Follow up on web drop-off', taskType: 'follow_up', priority: 'medium', dueDays: 1 }},
      { id: 'notify-staff', type: 'notifyNode', position: { x: X_CENTER, y: 1000 }, data: { message: 'Web drop-off not recovered after 48 hours — manual follow-up needed' }},
    ],
    edges: [
      mkEdge('trigger', 'delay-1h'), mkEdge('delay-1h', 'email-recover'), mkEdge('email-recover', 'delay-24h'),
      mkEdge('delay-24h', 'cond-opened'), mkEdge('cond-opened', 'sms-nudge', 'yes'), mkEdge('cond-opened', 'email-offer', 'no'),
      mkEdge('sms-nudge', 'task-followup'), mkEdge('email-offer', 'task-followup'), mkEdge('task-followup', 'notify-staff'),
    ],
  },
  {
    id: 'payment_recovery',
    name: 'Failed Payment Recovery',
    desc: 'Automated payment failure sequence: email alert, SMS reminder, staff notification with escalation',
    icon: CreditCard,
    color: '#ef4444',
    category: 'retention',
    triggerType: 'payment_failed',
    nodeCount: 8,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'payment_failed', triggerConfig: {}, exitCondition: 'goal_reached' }, deletable: false },
      { id: 'email-alert', type: 'emailNode', position: { x: X_CENTER, y: 140 }, data: { subject: 'Action needed: payment issue on your account', previewText: 'Please update your payment details', body: 'Hi {{first_name}},\n\nWe attempted to collect your monthly membership payment of {{amount_due}}, but unfortunately it wasn\'t successful.\n\nThis can happen for a number of reasons — expired card, insufficient funds, or a bank block.\n\nPlease update your payment details in your Member Hub to avoid any interruption to your membership.\n\nIf you need help, just reply to this email or call your local {{site_name}}.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true }},
      { id: 'tag-payment', type: 'tagNode', position: { x: X_CENTER, y: 280 }, data: { tagName: 'payment-issue' }},
      { id: 'delay-2d', type: 'delayNode', position: { x: X_CENTER, y: 400 }, data: { duration: 2, unit: 'days' }},
      { id: 'sms-remind', type: 'smsNode', position: { x: X_CENTER, y: 530 }, data: { message: 'Hi {{first_name}}, your UltraFlex payment of {{amount_due}} is overdue. Please update your details in the Member Hub to keep your access active.' }},
      { id: 'delay-5d', type: 'delayNode', position: { x: X_CENTER, y: 660 }, data: { duration: 5, unit: 'days' }},
      { id: 'notify-staff', type: 'notifyNode', position: { x: X_CENTER, y: 790 }, data: { message: 'Payment still not recovered after 7 days — escalate for manual review' }},
      { id: 'task-review', type: 'taskNode', position: { x: X_CENTER, y: 920 }, data: { taskSubject: 'Escalate failed payment — 7 days overdue', taskType: 'follow_up', priority: 'urgent', dueDays: 0 }},
    ],
    edges: [
      mkEdge('trigger', 'email-alert'), mkEdge('email-alert', 'tag-payment'), mkEdge('tag-payment', 'delay-2d'),
      mkEdge('delay-2d', 'sms-remind'), mkEdge('sms-remind', 'delay-5d'), mkEdge('delay-5d', 'notify-staff'),
      mkEdge('notify-staff', 'task-review'),
    ],
  },
  {
    id: 'new_member_onboarding',
    name: 'New Member Onboarding',
    desc: '14-day onboarding journey: welcome, getting started guide, PT upsell, check-in, and engagement tracking',
    icon: Rocket,
    color: '#22c55e',
    category: 'retention',
    triggerType: 'new_member',
    nodeCount: 11,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'new_member', triggerConfig: {}, exitCondition: 'max_duration', maxDurationDays: 30 }, deletable: false },
      { id: 'email-welcome', type: 'emailNode', position: { x: X_CENTER, y: 140 }, data: { subject: 'Welcome to the UltraFlex family, {{first_name}}!', previewText: 'Your membership is now active', body: 'Hi {{first_name}},\n\nCongratulations — you\'re officially an UltraFlex member! Your membership at {{site_name}} is now active.\n\nHere\'s what to do next:\n1. Download our app and log in with your member credentials\n2. Check out the class timetable for {{site_name}}\n3. Pop in for your free fitness assessment\n\nYour member number is {{member_number}}. Keep this handy for check-in.\n\nWe can\'t wait to see you in the gym!\n\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'tag-new', type: 'tagNode', position: { x: X_CENTER, y: 280 }, data: { tagName: 'new-member' }},
      { id: 'assign-staff', type: 'assignNode', position: { x: X_CENTER, y: 400 }, data: { assignMode: 'round_robin' }},
      { id: 'delay-1d', type: 'delayNode', position: { x: X_CENTER, y: 530 }, data: { duration: 1, unit: 'days', sendWindow: 'morning' }},
      { id: 'email-started', type: 'emailNode', position: { x: X_CENTER, y: 660 }, data: { subject: 'Your first week at UltraFlex — make it count', previewText: 'Tips to get the most out of your membership', body: 'Hi {{first_name}},\n\nYour first week is the most important! Here are some tips:\n\n- Start with 3 sessions this week — consistency beats intensity\n- Try a group class (they\'re all included in your membership)\n- Don\'t skip your free fitness assessment — book it at reception\n- The best time to train at {{site_name}} is usually early morning or late evening\n\nRemember, every expert was once a beginner. You\'ve got this!\n\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true }},
      { id: 'delay-3d', type: 'delayNode', position: { x: X_CENTER, y: 800 }, data: { duration: 3, unit: 'days' }},
      { id: 'email-pt', type: 'emailNode', position: { x: X_CENTER, y: 930 }, data: { subject: 'Supercharge your results with a PT session', previewText: 'Free taster session available', body: 'Hi {{first_name}},\n\nNow that you\'ve settled in, have you thought about personal training?\n\nOur certified PTs at {{site_name}} can:\n- Create a bespoke training programme for your goals\n- Teach proper form to prevent injuries\n- Keep you accountable and motivated\n\nAs a new member, you\'re eligible for a complimentary taster session. Ask at reception or book through the app.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-7d', type: 'delayNode', position: { x: X_CENTER, y: 1060 }, data: { duration: 7, unit: 'days' }},
      { id: 'cond-engaged', type: 'conditionNode', position: { x: X_CENTER, y: 1190 }, data: { conditionType: 'engagement_score' }},
      { id: 'notify-risk', type: 'notifyNode', position: { x: X_CENTER + 160, y: 1350 }, data: { message: 'New member not engaging after 14 days — follow up needed' }},
    ],
    edges: [
      mkEdge('trigger', 'email-welcome'), mkEdge('email-welcome', 'tag-new'), mkEdge('tag-new', 'assign-staff'),
      mkEdge('assign-staff', 'delay-1d'), mkEdge('delay-1d', 'email-started'), mkEdge('email-started', 'delay-3d'),
      mkEdge('delay-3d', 'email-pt'), mkEdge('email-pt', 'delay-7d'), mkEdge('delay-7d', 'cond-engaged'),
      mkEdge('cond-engaged', 'notify-risk', 'no'),
    ],
  },
  {
    id: 'cancellation_prevention',
    name: 'Cancellation Prevention',
    desc: 'At-risk member rescue: staff alert, personalised outreach, retention offer A/B test, and win-back',
    icon: Shield,
    color: '#f97316',
    category: 'retention',
    triggerType: 'cancellation_risk',
    nodeCount: 10,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'cancellation_risk', triggerConfig: {} }, deletable: false },
      { id: 'notify-staff', type: 'notifyNode', position: { x: X_CENTER, y: 130 }, data: { message: 'At-risk member detected — immediate attention required' }},
      { id: 'tag-risk', type: 'tagNode', position: { x: X_CENTER, y: 250 }, data: { tagName: 'at-risk' }},
      { id: 'delay-1h', type: 'delayNode', position: { x: X_CENTER, y: 370 }, data: { duration: 1, unit: 'hours' }},
      { id: 'email-save', type: 'emailNode', position: { x: X_CENTER, y: 500 }, data: { subject: 'We\'d hate to see you go, {{first_name}}', previewText: 'Can we help?', body: 'Hi {{first_name}},\n\nWe\'ve noticed you might be thinking of leaving UltraFlex, and we wanted to reach out personally.\n\nWhatever your reason — schedule, budget, goals — we\'d love the chance to help. Our team at {{site_name}} has solutions you might not know about:\n\n- Flexible freeze options if you need a break\n- Plan downgrades that still give you great access\n- Free PT consultations to refresh your programme\n- Off-peak memberships at reduced rates\n\nCould we have a quick chat? Reply to this email or call us — we\'re here to help.\n\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-2d', type: 'delayNode', position: { x: X_CENTER, y: 650 }, data: { duration: 2, unit: 'days' }},
      { id: 'task-call', type: 'taskNode', position: { x: X_CENTER, y: 780 }, data: { taskSubject: 'Retention call — at-risk member', taskType: 'follow_up', priority: 'urgent', dueDays: 0 }},
      { id: 'delay-3d', type: 'delayNode', position: { x: X_CENTER, y: 910 }, data: { duration: 3, unit: 'days' }},
      { id: 'ab-offer', type: 'abSplitNode', position: { x: X_CENTER, y: 1040 }, data: { splitA: 50, splitB: 50 }},
      { id: 'email-discount', type: 'emailNode', position: { x: X_CENTER - 160, y: 1190 }, data: { subject: 'A special offer to keep you with us', previewText: 'Exclusive retention offer inside', body: 'Hi {{first_name}},\n\nWe value you as a member and want to make it easy to stay. For a limited time, we\'re offering you a reduced rate on your membership.\n\nReply to this email to discuss your options with our team.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}' }},
    ],
    edges: [
      mkEdge('trigger', 'notify-staff'), mkEdge('notify-staff', 'tag-risk'), mkEdge('tag-risk', 'delay-1h'),
      mkEdge('delay-1h', 'email-save'), mkEdge('email-save', 'delay-2d'), mkEdge('delay-2d', 'task-call'),
      mkEdge('task-call', 'delay-3d'), mkEdge('delay-3d', 'ab-offer'),
      mkEdge('ab-offer', 'email-discount', 'pathA'),
    ],
  },
  {
    id: 'day_pass_conversion',
    name: 'Day Pass to Member Conversion',
    desc: 'Convert day pass visitors into members with feedback request, membership offer, and tour booking',
    icon: Ticket,
    color: '#8b5cf6',
    category: 'lead_gen',
    triggerType: 'day_pass_purchased',
    nodeCount: 9,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'day_pass_purchased', triggerConfig: {}, exitCondition: 'goal_reached' }, deletable: false },
      { id: 'tag-daypass', type: 'tagNode', position: { x: X_CENTER, y: 130 }, data: { tagName: 'day-pass-visitor' }},
      { id: 'delay-4h', type: 'delayNode', position: { x: X_CENTER, y: 250 }, data: { duration: 4, unit: 'hours' }},
      { id: 'email-feedback', type: 'emailNode', position: { x: X_CENTER, y: 380 }, data: { subject: 'How was your visit, {{first_name}}?', previewText: 'We hope you enjoyed UltraFlex', body: 'Hi {{first_name}},\n\nThanks for visiting {{site_name}} today! We hope you had a great workout.\n\nWe\'d love to hear how it went:\n- Were the facilities what you expected?\n- Did our team look after you?\n- Is there anything we could improve?\n\nJust reply to this email with your thoughts — we read every response.\n\nSee you again soon!\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-2d', type: 'delayNode', position: { x: X_CENTER, y: 520 }, data: { duration: 2, unit: 'days', sendWindow: 'morning' }},
      { id: 'email-offer', type: 'emailNode', position: { x: X_CENTER, y: 650 }, data: { subject: 'Ready to make it official, {{first_name}}?', previewText: 'Join UltraFlex — special day pass visitor offer', body: 'Hi {{first_name}},\n\nNow you\'ve experienced UltraFlex first-hand, why not make it a regular thing?\n\nAs a day pass visitor, here\'s what you get when you join:\n- No joining fee (saving you money from day one)\n- Access to all classes at {{site_name}}\n- Flexible monthly membership — cancel anytime\n- Free fitness assessment with a certified PT\n\nJoin online now or pop in and we\'ll get you set up in minutes.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-5d', type: 'delayNode', position: { x: X_CENTER, y: 790 }, data: { duration: 5, unit: 'days' }},
      { id: 'cond-clicked', type: 'conditionNode', position: { x: X_CENTER, y: 920 }, data: { conditionType: 'email_clicked' }},
      { id: 'task-call', type: 'taskNode', position: { x: X_CENTER - 160, y: 1080 }, data: { taskSubject: 'Call day pass visitor — showed interest', taskType: 'follow_up', priority: 'high', dueDays: 1 }},
    ],
    edges: [
      mkEdge('trigger', 'tag-daypass'), mkEdge('tag-daypass', 'delay-4h'), mkEdge('delay-4h', 'email-feedback'),
      mkEdge('email-feedback', 'delay-2d'), mkEdge('delay-2d', 'email-offer'), mkEdge('email-offer', 'delay-5d'),
      mkEdge('delay-5d', 'cond-clicked'), mkEdge('cond-clicked', 'task-call', 'yes'),
    ],
  },
  {
    id: 'tour_follow_up',
    name: 'Post-Tour Nurture',
    desc: 'After a gym tour: thank you, limited-time joining offer, conditional follow-up based on engagement',
    icon: Building2,
    color: '#6366f1',
    category: 'lead_gen',
    triggerType: 'tour_completed',
    nodeCount: 9,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'tour_completed', triggerConfig: {}, exitCondition: 'goal_reached' }, deletable: false },
      { id: 'email-thanks', type: 'emailNode', position: { x: X_CENTER, y: 140 }, data: { subject: 'Great meeting you today, {{first_name}}!', previewText: 'Thanks for touring UltraFlex', body: 'Hi {{first_name}},\n\nIt was great showing you around {{site_name}} today! We hope you liked what you saw.\n\nJust a quick reminder of what\'s included when you join:\n- Full access to all equipment and facilities\n- Unlimited group classes\n- Free fitness assessment\n- Flexible membership — no long-term commitment\n\nReady to join? You can sign up online or pop back in — we\'d love to welcome you properly.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'tag-toured', type: 'tagNode', position: { x: X_CENTER, y: 280 }, data: { tagName: 'toured' }},
      { id: 'stage-done', type: 'stageNode', position: { x: X_CENTER, y: 400 }, data: { targetStage: 'tour_done' }},
      { id: 'delay-1d', type: 'delayNode', position: { x: X_CENTER, y: 530 }, data: { duration: 1, unit: 'days', sendWindow: 'morning' }},
      { id: 'email-offer', type: 'emailNode', position: { x: X_CENTER, y: 660 }, data: { subject: 'Your tour offer expires soon, {{first_name}}', previewText: 'Join this week and save', body: 'Hi {{first_name}},\n\nFollowing your tour at {{site_name}}, we\'re offering you an exclusive deal:\n\n- Join within 7 days of your tour\n- Pay no joining fee\n- Get your first month at a discounted rate\n\nThis offer is only available to tour visitors and expires soon, so don\'t wait too long!\n\nSign up online or visit us at {{site_name}}.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-3d', type: 'delayNode', position: { x: X_CENTER, y: 800 }, data: { duration: 3, unit: 'days' }},
      { id: 'cond-clicked', type: 'conditionNode', position: { x: X_CENTER, y: 930 }, data: { conditionType: 'email_clicked' }},
      { id: 'task-call', type: 'taskNode', position: { x: X_CENTER - 160, y: 1090 }, data: { taskSubject: 'Follow up post-tour — hasn\'t joined yet', taskType: 'follow_up', priority: 'high', dueDays: 1 }},
    ],
    edges: [
      mkEdge('trigger', 'email-thanks'), mkEdge('email-thanks', 'tag-toured'), mkEdge('tag-toured', 'stage-done'),
      mkEdge('stage-done', 'delay-1d'), mkEdge('delay-1d', 'email-offer'), mkEdge('email-offer', 'delay-3d'),
      mkEdge('delay-3d', 'cond-clicked'), mkEdge('cond-clicked', 'task-call', 'no'),
    ],
  },
  {
    id: 'inactivity_reengagement',
    name: 'Inactivity Re-Engagement',
    desc: '30-day inactivity trigger: "we miss you" email, engagement check, SMS nudge, staff escalation',
    icon: TimerOff,
    color: '#6b7280',
    category: 'retention',
    triggerType: 'inactivity',
    nodeCount: 9,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'inactivity', triggerConfig: { days: 30 } }, deletable: false },
      { id: 'tag-inactive', type: 'tagNode', position: { x: X_CENTER, y: 130 }, data: { tagName: 'inactive-30d' }},
      { id: 'email-miss', type: 'emailNode', position: { x: X_CENTER, y: 260 }, data: { subject: 'We miss you at UltraFlex, {{first_name}}!', previewText: 'It\'s been a while — come back and see what\'s new', body: 'Hi {{first_name}},\n\nIt\'s been a little while since we\'ve seen you at {{site_name}}, and we just wanted to check in.\n\nLife gets busy — we totally get it. But your membership is still active and waiting for you. Here\'s what\'s new:\n\n- New equipment added to the gym floor\n- Fresh class timetable with evening and weekend options\n- Free "comeback" PT session to get you back on track\n\nSometimes the hardest part is just showing up. We\'ll be here when you\'re ready.\n\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-3d', type: 'delayNode', position: { x: X_CENTER, y: 400 }, data: { duration: 3, unit: 'days' }},
      { id: 'cond-opened', type: 'conditionNode', position: { x: X_CENTER, y: 530 }, data: { conditionType: 'email_opened' }},
      { id: 'sms-nudge', type: 'smsNode', position: { x: X_CENTER + 160, y: 690 }, data: { message: 'Hi {{first_name}}, we miss you at {{site_name}}! Your free comeback PT session is waiting. Pop in this week and let us help you get back on track.' }},
      { id: 'delay-7d', type: 'delayNode', position: { x: X_CENTER, y: 830 }, data: { duration: 7, unit: 'days' }},
      { id: 'notify-staff', type: 'notifyNode', position: { x: X_CENTER, y: 960 }, data: { message: 'Inactive member for 40+ days — personal outreach recommended' }},
      { id: 'task-reach', type: 'taskNode', position: { x: X_CENTER, y: 1090 }, data: { taskSubject: 'Personal outreach — long-term inactive member', taskType: 'follow_up', priority: 'high', dueDays: 1 }},
    ],
    edges: [
      mkEdge('trigger', 'tag-inactive'), mkEdge('tag-inactive', 'email-miss'), mkEdge('email-miss', 'delay-3d'),
      mkEdge('delay-3d', 'cond-opened'), mkEdge('cond-opened', 'sms-nudge', 'no'),
      mkEdge('sms-nudge', 'delay-7d'),
      mkEdge('delay-7d', 'notify-staff'), mkEdge('notify-staff', 'task-reach'),
    ],
  },
  {
    id: 'contact_form_response',
    name: 'Contact Form Auto-Response',
    desc: 'Instant acknowledgement, round-robin staff assignment, callback task with escalation timer',
    icon: Inbox,
    color: '#14b8a6',
    category: 'lead_gen',
    triggerType: 'contact_form',
    nodeCount: 7,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'contact_form', triggerConfig: {} }, deletable: false },
      { id: 'assign-rr', type: 'assignNode', position: { x: X_CENTER, y: 130 }, data: { assignMode: 'round_robin' }},
      { id: 'notify-staff', type: 'notifyNode', position: { x: X_CENTER, y: 260 }, data: { message: 'New website enquiry received — please respond within 30 minutes' }},
      { id: 'delay-5m', type: 'delayNode', position: { x: X_CENTER, y: 390 }, data: { duration: 5, unit: 'minutes' }},
      { id: 'email-ack', type: 'emailNode', position: { x: X_CENTER, y: 520 }, data: { subject: 'Thanks for getting in touch, {{first_name}}!', previewText: 'We\'ve received your enquiry', body: 'Hi {{first_name}},\n\nThank you for contacting UltraFlex! We\'ve received your enquiry and a member of our team will be in touch shortly.\n\nIn the meantime, here are some quick links:\n- View our membership options\n- Book a free gym tour\n- Check out our class timetable\n\nWe aim to respond to all enquiries within 2 hours during business hours.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true }},
      { id: 'task-callback', type: 'taskNode', position: { x: X_CENTER, y: 660 }, data: { taskSubject: 'Respond to website enquiry', taskType: 'follow_up', priority: 'high', dueDays: 0 }},
      { id: 'stage-contacted', type: 'stageNode', position: { x: X_CENTER, y: 790 }, data: { targetStage: 'contacted' }},
    ],
    edges: [
      mkEdge('trigger', 'assign-rr'), mkEdge('assign-rr', 'notify-staff'), mkEdge('notify-staff', 'delay-5m'),
      mkEdge('delay-5m', 'email-ack'), mkEdge('email-ack', 'task-callback'), mkEdge('task-callback', 'stage-contacted'),
    ],
  },
  {
    id: 'lead_qualification',
    name: 'Lead Qualification & Scoring',
    desc: 'Multi-stage qualification: engagement tracking, conditional tour booking push, A/B tested messaging',
    icon: TrendingUp,
    color: '#06b6d4',
    category: 'lead_gen',
    triggerType: 'stage_change',
    nodeCount: 10,
    nodes: [
      { id: 'trigger', type: 'triggerNode', position: { x: X_CENTER, y: 0 }, data: { triggerType: 'stage_change', triggerConfig: { target_stage: 'contacted' } }, deletable: false },
      { id: 'delay-1d', type: 'delayNode', position: { x: X_CENTER, y: 130 }, data: { duration: 1, unit: 'days', sendWindow: 'business_hours' }},
      { id: 'email-qualify', type: 'emailNode', position: { x: X_CENTER, y: 260 }, data: { subject: 'What are your fitness goals, {{first_name}}?', previewText: 'Help us help you', body: 'Hi {{first_name}},\n\nNow that we\'ve connected, we\'d love to understand your fitness goals better so we can recommend the perfect plan.\n\nAre you looking to:\n- Build strength and muscle?\n- Lose weight and tone up?\n- Improve overall fitness and wellbeing?\n- Train for a specific event or sport?\n\nJust reply to this email or book a free consultation at {{site_name}} — we\'ll match you with the right membership and support.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}', trackOpens: true, trackClicks: true }},
      { id: 'delay-3d', type: 'delayNode', position: { x: X_CENTER, y: 400 }, data: { duration: 3, unit: 'days' }},
      { id: 'cond-engaged', type: 'conditionNode', position: { x: X_CENTER, y: 530 }, data: { conditionType: 'email_clicked' }},
      { id: 'stage-qualified', type: 'stageNode', position: { x: X_CENTER - 160, y: 690 }, data: { targetStage: 'qualified' }},
      { id: 'email-tour', type: 'emailNode', position: { x: X_CENTER - 160, y: 830 }, data: { subject: 'See UltraFlex for yourself — book a tour', previewText: 'Free guided tour at your local gym', body: 'Hi {{first_name}},\n\nThere\'s nothing quite like seeing UltraFlex in person. We\'d love to give you a personal guided tour of {{site_name}}.\n\nYou\'ll see:\n- Our full range of cardio and strength equipment\n- The group fitness studios\n- Changing facilities and amenities\n- Meet some of our friendly team\n\nBook your free tour today — no obligation, no pressure.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}' }},
      { id: 'ab-msg', type: 'abSplitNode', position: { x: X_CENTER + 160, y: 690 }, data: { splitA: 50, splitB: 50 }},
      { id: 'sms-push', type: 'smsNode', position: { x: X_CENTER + 40, y: 850 }, data: { message: 'Hi {{first_name}}, fancy a look around {{site_name}}? Book a free tour and see why our members love it here!' }},
      { id: 'email-social', type: 'emailNode', position: { x: X_CENTER + 280, y: 850 }, data: { subject: 'What our members say about UltraFlex', previewText: 'Real stories from real members', body: 'Hi {{first_name}},\n\nDon\'t just take our word for it — here\'s what our members have to say about life at UltraFlex:\n\n"Best gym I\'ve ever been to. The staff are amazing and the facilities are top-notch." — Sarah, member since 2024\n\n"I was nervous about joining, but everyone made me feel welcome from day one." — James, member since 2025\n\nReady to write your own story? Join us at {{site_name}}.\n\nBest,\nThe UltraFlex Team\n\n{{unsubscribe_link}}' }},
    ],
    edges: [
      mkEdge('trigger', 'delay-1d'), mkEdge('delay-1d', 'email-qualify'), mkEdge('email-qualify', 'delay-3d'),
      mkEdge('delay-3d', 'cond-engaged'),
      mkEdge('cond-engaged', 'stage-qualified', 'yes'), mkEdge('cond-engaged', 'ab-msg', 'no'),
      mkEdge('stage-qualified', 'email-tour'),
      mkEdge('ab-msg', 'sms-push', 'pathA'), mkEdge('ab-msg', 'email-social', 'pathB'),
    ],
  },
];

const TEMPLATE_CATEGORIES = [
  { id: 'all', label: 'All Templates' },
  { id: 'starter', label: 'Getting Started' },
  { id: 'lead_gen', label: 'Lead Generation' },
  { id: 'retention', label: 'Retention' },
];

// ===================== TEMPLATE GALLERY =====================
const TemplateGallery = ({ onSelect, onClose }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredId, setHoveredId] = useState(null);

  const filtered = selectedCategory === 'all'
    ? JOURNEY_TEMPLATES
    : JOURNEY_TEMPLATES.filter(t => t.category === selectedCategory);

  const getCategoryBadge = (cat) => {
    const map = {
      starter: { label: 'Starter', bg: 'bg-gray-500/20', text: 'text-gray-400' },
      lead_gen: { label: 'Lead Gen', bg: 'bg-blue-500/20', text: 'text-blue-400' },
      retention: { label: 'Retention', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
    };
    return map[cat] || map.starter;
  };

  return (
    <div className="h-screen flex flex-col bg-[#0b0f1a]" data-testid="template-gallery">
      {/* Header */}
      <div className="h-14 bg-[#111827] border-b border-[#1e293b] flex items-center justify-between px-6 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#1e293b] text-gray-400 hover:text-white" data-testid="gallery-back">
            <ChevronLeft size={18} />
          </button>
          <div className="h-6 w-px bg-[#1e293b]" />
          <LayoutTemplate size={18} className="text-[#dc2626]" />
          <h1 className="text-white font-semibold text-sm">Choose a Template</h1>
        </div>
        <p className="text-gray-500 text-xs">{filtered.length} templates available</p>
      </div>

      {/* Category Tabs */}
      <div className="bg-[#111827] border-b border-[#1e293b] px-6 py-3 flex gap-2 flex-shrink-0">
        {TEMPLATE_CATEGORIES.map(cat => (
          <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedCategory === cat.id ? 'bg-[#dc2626] text-white' : 'bg-[#1e293b] text-gray-400 hover:text-white hover:bg-[#2a3a5a]'}`}
            data-testid={`template-cat-${cat.id}`}>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Template Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-w-7xl mx-auto">
          {filtered.map(template => {
            const Icon = template.icon;
            const badge = getCategoryBadge(template.category);
            const isHovered = hoveredId === template.id;
            const isBlank = template.id === 'blank';

            return (
              <button key={template.id}
                onClick={() => onSelect(template)}
                onMouseEnter={() => setHoveredId(template.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`group text-left rounded-xl border transition-all duration-200 overflow-hidden ${isHovered ? 'border-[#dc2626] shadow-lg shadow-[#dc2626]/10 scale-[1.02]' : 'border-[#1e293b] hover:border-[#2a3a5a]'} ${isBlank ? 'border-dashed' : ''}`}
                style={{ background: isHovered ? '#111827' : '#0f172a' }}
                data-testid={`template-${template.id}`}>

                {/* Color accent bar */}
                <div className="h-1" style={{ background: isHovered ? template.color : 'transparent', transition: 'background 0.2s' }} />

                <div className="p-5">
                  {/* Icon + Badge row */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2.5 rounded-xl" style={{ background: `${template.color}15` }}>
                      <Icon size={20} style={{ color: template.color }} />
                    </div>
                    {!isBlank && (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${badge.bg} ${badge.text}`}>{badge.label}</span>
                    )}
                  </div>

                  {/* Name + Desc */}
                  <h3 className="text-white font-semibold text-sm mb-1.5 group-hover:text-[#dc2626] transition-colors">{template.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{template.desc}</p>

                  {/* Stats row */}
                  {!isBlank && (
                    <div className="flex items-center gap-3 text-[10px]">
                      <span className="text-gray-500">{template.nodes.length - 1} steps</span>
                      <span className="text-gray-600">|</span>
                      <span className="text-gray-500">{template.nodes.filter(n => n.type === 'emailNode').length} emails</span>
                      {template.nodes.some(n => n.type === 'smsNode') && (
                        <><span className="text-gray-600">|</span><span className="text-gray-500">{template.nodes.filter(n => n.type === 'smsNode').length} SMS</span></>
                      )}
                      {template.nodes.some(n => n.type === 'conditionNode') && (
                        <><span className="text-gray-600">|</span><span className="text-gray-500">{template.nodes.filter(n => n.type === 'conditionNode').length} conditions</span></>
                      )}
                      {template.nodes.some(n => n.type === 'abSplitNode') && (
                        <><span className="text-gray-600">|</span><span className="text-purple-400">A/B test</span></>
                      )}
                    </div>
                  )}
                  {isBlank && (
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                      <Plus size={12} />
                      <span>Start with an empty canvas</span>
                    </div>
                  )}
                </div>

                {/* CTA bar */}
                <div className={`px-5 py-3 border-t transition-all ${isHovered ? 'border-[#dc2626]/30 bg-[#dc2626]/5' : 'border-[#1e293b]'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${isHovered ? 'text-[#dc2626]' : 'text-gray-500'}`}>
                      {isBlank ? 'Create Blank' : 'Use Template'}
                    </span>
                    <ArrowRight size={14} className={`transition-all ${isHovered ? 'text-[#dc2626] translate-x-0.5' : 'text-gray-600'}`} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ===================== MAIN BUILDER =====================
const AutomationBuilderInner = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { journeyId } = useParams();
  const headers = useMemo(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const { screenToFlowPosition } = useReactFlow();
  const reactFlowWrapper = useRef(null);

  const [journeyName, setJourneyName] = useState('New Journey');
  const [isActive, setIsActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [activeView, setActiveView] = useState('canvas');
  const [staffList, setStaffList] = useState([]);
  const [showTemplates, setShowTemplates] = useState(journeyId === 'new');
  const [emailDesignerNode, setEmailDesignerNode] = useState(null);

  const defaultNodes = [
    { id: 'trigger', type: 'triggerNode', position: { x: 250, y: 50 }, data: { triggerType: 'lead_created', triggerConfig: {} }, deletable: false },
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(defaultNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Load staff list
  useEffect(() => {
    fetch(`${API_URL}/api/staff?page_size=100`, { headers }).then(r => r.json()).then(d => setStaffList(d.items || [])).catch(() => {});
  }, []);

  // Load existing journey
  useEffect(() => {
    if (journeyId && journeyId !== 'new') {
      fetch(`${API_URL}/api/crm/automations/${journeyId}`, { headers })
        .then(r => r.json())
        .then(data => {
          setJourneyName(data.name || 'Untitled');
          setIsActive(data.is_active || false);
          if (data.workflow_nodes?.length > 0) {
            const loadedNodes = data.workflow_nodes.map(n => ({ ...n, data: { ...n.data, onDelete: deleteNode } }));
            setNodes(loadedNodes);
            setEdges(data.workflow_edges || []);
          } else if (data.steps?.length > 0) {
            // Legacy: convert flat steps to nodes
            const converted = convertLegacySteps(data);
            setNodes(converted.nodes);
            setEdges(converted.edges);
          }
          if (data.trigger_type) {
            setNodes(prev => prev.map(n => n.id === 'trigger' ? { ...n, data: { ...n.data, triggerType: data.trigger_type, triggerConfig: data.trigger_config || {} } } : n));
          }
        })
        .catch(() => {});
    }
  }, [journeyId]);

  const convertLegacySteps = (journey) => {
    const ns = [{ id: 'trigger', type: 'triggerNode', position: { x: 250, y: 50 }, data: { triggerType: journey.trigger_type, triggerConfig: journey.trigger_config || {} }, deletable: false }];
    const es = [];
    (journey.steps || []).forEach((step, i) => {
      const yPos = 180 + i * 140;
      const nodeId = `legacy-${i}`;
      const typeMap = { email: 'emailNode', sms: 'smsNode', task: 'taskNode', change_stage: 'stageNode', assign: 'assignNode', tag: 'tagNode', notification: 'notifyNode' };
      const nType = typeMap[step.action_type] || 'emailNode';
      // Add delay node if delay > 0
      if (step.delay_hours > 0) {
        const delayId = `delay-${i}`;
        ns.push({ id: delayId, type: 'delayNode', position: { x: 250, y: yPos - 70 }, data: { duration: step.delay_hours, unit: 'hours', onDelete: deleteNode } });
        es.push({ id: `e-${i > 0 ? `legacy-${i-1}` : 'trigger'}-${delayId}`, source: i > 0 ? `legacy-${i-1}` : 'trigger', target: delayId, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } });
        es.push({ id: `e-${delayId}-${nodeId}`, source: delayId, target: nodeId, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } });
      } else {
        es.push({ id: `e-${i > 0 ? `legacy-${i-1}` : 'trigger'}-${nodeId}`, source: i > 0 ? `legacy-${i-1}` : 'trigger', target: nodeId, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } });
      }
      ns.push({ id: nodeId, type: nType, position: { x: 250, y: yPos }, data: { subject: step.template_subject, body: step.template_body, previewText: step.template_body?.slice(0, 60), taskSubject: step.template_subject, targetStage: step.config?.target_stage, onDelete: deleteNode, ...step.config } });
    });
    return { nodes: ns, edges: es };
  };

  const deleteNode = useCallback((nodeId) => {
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
  }, [setNodes, setEdges]);

  const onConnect = useCallback((params) => {
    setEdges(prev => addEdge({ ...params, type: 'smoothstep', animated: true, style: { stroke: '#4a5568', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4a5568' } }, prev));
  }, [setEdges]);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, []);

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...newData, onDelete: deleteNode } } : n));
    setSelectedNode(prev => prev && prev.id === nodeId ? { ...prev, data: { ...prev.data, ...newData } } : prev);
  }, [setNodes, deleteNode]);

  // Drag and drop from palette
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData('application/reactflow');
    if (!type) return;
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const newId = `${type}-${Date.now()}`;
    const newNode = {
      id: newId,
      type,
      position,
      data: { onDelete: deleteNode },
    };
    setNodes(prev => [...prev, newNode]);
  }, [screenToFlowPosition, setNodes, deleteNode]);

  // Load template into canvas
  const loadTemplate = useCallback((template) => {
    if (template.id === 'blank') {
      setNodes([{ id: 'trigger', type: 'triggerNode', position: { x: 250, y: 50 }, data: { triggerType: 'lead_created', triggerConfig: {}, onDelete: deleteNode }, deletable: false }]);
      setEdges([]);
      setJourneyName('New Journey');
    } else {
      const templateNodes = template.nodes.map(n => ({
        ...n,
        data: { ...n.data, onDelete: deleteNode },
      }));
      setNodes(templateNodes);
      setEdges(template.edges || []);
      setJourneyName(template.name);
    }
    setShowTemplates(false);
    setSelectedNode(null);
  }, [setNodes, setEdges, deleteNode]);

  // Show template gallery for new journeys
  if (showTemplates) {
    return <TemplateGallery onSelect={loadTemplate} onClose={() => navigate('/admin/crm')} />;
  }

  // Email Designer handlers
  const handleOpenEmailDesigner = (node) => setEmailDesignerNode(node);

  const handleSaveEmailDesign = (designData) => {
    if (emailDesignerNode) {
      updateNodeData(emailDesignerNode.id, 'emailBlocks', designData.emailBlocks);
      updateNodeData(emailDesignerNode.id, 'brand', designData.brand);
      updateNodeData(emailDesignerNode.id, 'emailHtml', designData.emailHtml);
      if (designData.body) updateNodeData(emailDesignerNode.id, 'body', designData.body);
    }
    setEmailDesignerNode(null);
  };

  // Show email designer overlay
  if (emailDesignerNode) {
    return <EmailDesigner data={emailDesignerNode.data} onSave={handleSaveEmailDesign} onClose={() => setEmailDesignerNode(null)} token={token} />;
  }

  // Save journey
  const saveJourney = async () => {
    setSaving(true);
    const triggerNode = nodes.find(n => n.type === 'triggerNode');
    const payload = {
      name: journeyName,
      trigger_type: triggerNode?.data?.triggerType || 'lead_created',
      trigger_config: triggerNode?.data?.triggerConfig || {},
      is_active: isActive,
      workflow_nodes: nodes.map(n => ({ id: n.id, type: n.type, position: n.position, data: { ...n.data, onDelete: undefined } })),
      workflow_edges: edges,
      // Also save legacy steps format for backward compatibility
      steps: nodes.filter(n => !['triggerNode'].includes(n.type)).map(n => ({
        action_type: n.type.replace('Node', ''),
        delay_hours: n.type === 'delayNode' ? (n.data.duration || 1) : 0,
        template_subject: n.data.subject || n.data.taskSubject || '',
        template_body: n.data.body || n.data.message || '',
        config: { target_stage: n.data.targetStage, tag: n.data.tagName, assignee_id: n.data.assigneeId },
      })),
      exit_condition: triggerNode?.data?.exitCondition,
      max_duration_days: triggerNode?.data?.maxDurationDays,
    };

    try {
      const url = journeyId && journeyId !== 'new'
        ? `${API_URL}/api/crm/automations/${journeyId}`
        : `${API_URL}/api/crm/automations`;
      const method = journeyId && journeyId !== 'new' ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        const data = await res.json();
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (!journeyId || journeyId === 'new') {
          navigate(`/admin/automations/${data.id}`, { replace: true });
        }
      }
    } catch {}
    setSaving(false);
  };

  return (
    <div className="h-screen flex flex-col bg-[#0b0f1a]" data-testid="automation-builder">
      {/* Top Bar */}
      <div className="h-14 bg-[#111827] border-b border-[#1e293b] flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/crm')} className="p-1.5 rounded-lg hover:bg-[#1e293b] text-gray-400 hover:text-white" data-testid="back-to-crm">
            <ChevronLeft size={18} />
          </button>
          <div className="h-6 w-px bg-[#1e293b]" />
          <Zap size={18} className="text-[#dc2626]" />
          <input value={journeyName} onChange={e => setJourneyName(e.target.value)}
            className="bg-transparent text-white font-semibold text-sm border-none outline-none w-64" data-testid="journey-name-input" />
        </div>
        <div className="flex items-center gap-2">
          {/* View Tabs */}
          {['canvas', 'enrollments', 'analytics'].map(v => (
            <button key={v} onClick={() => setActiveView(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize ${activeView === v ? 'bg-[#dc2626] text-white' : 'text-gray-400 hover:text-white hover:bg-[#1e293b]'}`}
              data-testid={`view-${v}`}>
              {v === 'canvas' && <Settings2 size={12} className="inline mr-1" />}
              {v === 'enrollments' && <Users size={12} className="inline mr-1" />}
              {v === 'analytics' && <BarChart3 size={12} className="inline mr-1" />}
              {v}
            </button>
          ))}
          <div className="h-6 w-px bg-[#1e293b] mx-1" />
          <button onClick={() => setIsActive(!isActive)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}
            data-testid="toggle-active">
            {isActive ? <><Play size={12} />Live</> : <><Pause size={12} />Draft</>}
          </button>
          <button onClick={saveJourney} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#dc2626] text-white rounded-lg text-xs font-semibold hover:bg-[#b91c1c] disabled:opacity-50"
            data-testid="save-journey-btn">
            {saving ? <RefreshCw size={12} className="animate-spin" /> : saved ? <Check size={12} /> : <Save size={12} />}
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {activeView === 'canvas' && (
          <>
            {/* Node Palette Sidebar */}
            <div className="w-56 bg-[#111827] border-r border-[#1e293b] overflow-y-auto flex-shrink-0 p-3" data-testid="node-palette">
              <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-3">Drag to canvas</p>
              {['action', 'timing', 'logic', 'control'].map(cat => (
                <div key={cat} className="mb-4">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wider mb-1.5 px-1">{cat}</p>
                  <div className="space-y-1">
                    {NODE_PALETTE.filter(n => n.category === cat).map(item => {
                      const Icon = item.icon;
                      return (
                        <div key={item.type} draggable
                          onDragStart={e => { e.dataTransfer.setData('application/reactflow', item.type); e.dataTransfer.effectAllowed = 'move'; }}
                          className="flex items-center gap-2.5 p-2 rounded-lg cursor-grab active:cursor-grabbing hover:bg-[#1e293b] transition-colors group"
                          data-testid={`palette-${item.type}`}>
                          <div style={{ background: `${item.color}20`, borderRadius: '6px', padding: '5px', display: 'flex' }}>
                            <Icon size={14} style={{ color: item.color }} />
                          </div>
                          <span className="text-gray-300 text-xs font-medium group-hover:text-white">{item.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Canvas */}
            <div className="flex-1" ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={onNodeClick}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onPaneClick={() => setSelectedNode(null)}
                nodeTypes={nodeTypes}
                fitView
                defaultEdgeOptions={{ type: 'smoothstep', animated: true, style: { stroke: '#4a5568', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: '#4a5568' } }}
                style={{ background: '#0b0f1a' }}
                proOptions={{ hideAttribution: true }}
              >
                <Background color="#1e293b" gap={20} size={1} />
                <Controls style={{ background: '#111827', borderColor: '#1e293b', borderRadius: '8px' }} />
                <MiniMap style={{ background: '#111827', border: '1px solid #1e293b', borderRadius: '8px' }} nodeColor={n => {
                  const palette = NODE_PALETTE.find(p => p.type === n.type);
                  return palette?.color || '#6b7280';
                }} />
                {nodes.length <= 1 && (
                  <Panel position="top-center">
                    <div className="bg-[#111827]/90 backdrop-blur border border-[#1e293b] rounded-xl px-6 py-4 text-center mt-8">
                      <p className="text-white font-medium text-sm">Drag nodes from the left panel to build your workflow</p>
                      <p className="text-gray-500 text-xs mt-1">Connect nodes by dragging from one handle to another</p>
                    </div>
                  </Panel>
                )}
              </ReactFlow>
            </div>

            {/* Properties Panel */}
            {selectedNode && (
              <PropertiesPanel node={selectedNode} onUpdate={updateNodeData} onClose={() => setSelectedNode(null)} staffList={staffList} onOpenEmailDesigner={handleOpenEmailDesigner} />
            )}
          </>
        )}

        {activeView === 'enrollments' && (
          <div className="flex-1 p-6 overflow-y-auto">
            <EnrollmentDashboard journeyId={journeyId} headers={headers} />
          </div>
        )}

        {activeView === 'analytics' && (
          <div className="flex-1 p-6 overflow-y-auto">
            <JourneyAnalytics journeyId={journeyId} nodes={nodes} headers={headers} />
          </div>
        )}
      </div>
    </div>
  );
};

const AutomationBuilder = () => (
  <ReactFlowProvider>
    <AutomationBuilderInner />
  </ReactFlowProvider>
);

export default AutomationBuilder;
